class IndexedDBManager {
	constructor () {
		this.db = null
		this.dbName = 'ChatDB'
		this.version = 3
		this.isInitializing = false
		this.initPromise = null

		this.latestTimestamps = new Map()
	}

	static getInstance () {
		if (!IndexedDBManager.instance) {
			IndexedDBManager.instance = new IndexedDBManager()
		}
		return IndexedDBManager.instance
	}

	async init (config = {}) {
		if (this.isInitializing) {
			return this.initPromise
		}

		if (this.db) {
			return this.db
		}

		this.isInitializing = true
		this.dbName = config.dbName || this.dbName
		this.version = config.version || this.version

		this.initPromise = new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, this.version)

			request.onupgradeneeded = (event) => {
				const db = event.target.result

				if (db.objectStoreNames.contains('messages')) {
					db.deleteObjectStore('messages')
				}

				const store = db.createObjectStore('messages', {keyPath: 'id'})
				store.createIndex('stage_id', 'stage_id', {unique: false})
				store.createIndex('timestamp', 'timestamp', {unique: false})
				store.createIndex('stage_time', ['stage_id', 'timestamp'], {unique: false})
				store.createIndex('stage_role', ['stage_id', 'role_id'], {unique: false})
			}

			request.onsuccess = (event) => {
				this.db = event.target.result
				this.isInitializing = false
				resolve(this.db)
			}

			request.onerror = (event) => {
				this.isInitializing = false
				reject(event.target.error)
			}
		})

		return this.initPromise
	}

	getTransaction (storeName, mode = 'readonly') {
		if (!this.db) {
			throw new Error('Database not initialized. Call init() first.')
		}
		return this.db.transaction(storeName, mode)
	}

	async add (storeName, data) {
		await this.init()

		if (!(await this.shouldInsertMessage(storeName, data))) {
			console.log(`消息 ${data.message_id} 已存在或不是最新的，跳过插入`)
			return data.id
		}

		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName, 'readwrite')
			const store = transaction.objectStore(storeName)
			data['id'] = data['message_id']
			delete data.message_id
			// console.log(`插入数据 ${JSON.stringify(data)}`)
			const request = store.add(data)

			request.onsuccess = async () => {
				await this.updateLatestTimestamp(storeName, data.stage_id, data.timestamp)
				resolve(request.result)
			}
			request.onerror = () => reject(request.error)
		})
	}

	async bulkAdd (storeName, items, force = false) {
		await this.init()

		let messagesToInsert
		if (force) {
			messagesToInsert = items
		} else {
			messagesToInsert = await this.filterMessagesToInsert(storeName, items)
		}

		if (messagesToInsert.length === 0) {
			console.log('没有新消息需要插入')
			return []
		}

		const transaction = this.getTransaction(storeName, 'readwrite')
		const store = transaction.objectStore(storeName)

		const results = []
		const errors = []
		await Promise.all(messagesToInsert.map(item => {
			return new Promise((resolve) => {
				if (!Object.keys(item).includes('message_id')) resolve()
				item['id'] = item['message_id']
				// console.log(`put item = ${JSON.stringify(item)}`)
				const request = store.put(item)

				request.onsuccess = async () => {
					await this.updateLatestTimestamp(storeName, item.stage_id, item.timestamp)
					results.push(request.result)
					resolve()
				}

				request.onerror = () => {
					errors.push(request.error)
					resolve()
				}
			})
		}))

		if (errors.length > 0) {
			console.warn(`${errors.length} 条消息插入失败 ${errors}`)
		}

		// console.log(`成功插入 ${results.length}/${items.length} 条消息`)
		return results
	}

	async put (storeName, data) {
		await this.init()

		if (!(await this.shouldInsertMessage(storeName, data))) {
			console.log(`消息 ${data.message_id} 不是最新的，跳过更新`)
			return data.id
		}

		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName, 'readwrite')
			const store = transaction.objectStore(storeName)
			data['id'] = data['message_id']
			const request = store.put(data)

			request.onsuccess = async () => {
				await this.updateLatestTimestamp(storeName, data.stage_id, data.timestamp)
				resolve(request.result)
			}
			request.onerror = () => reject(request.error)
		})
	}

	async get (storeName, id) {
		await this.init()
		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName)
			const store = transaction.objectStore(storeName)
			const request = store.get(id)

			request.onsuccess = () => resolve(request.result)
			request.onerror = () => reject(request.error)
		})
	}

	async getByIndex (storeName, indexName, value) {
		await this.init()
		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName)
			const store = transaction.objectStore(storeName)
			const index = store.index(indexName)
			const request = index.getAll(value)

			request.onsuccess = () => resolve(request.result)
			request.onerror = () => reject(request.error)
		})
	}

	async query (storeName, options = {}) {
		await this.init()
		const {
			indexName,
			range,
			limit = 20,
			offset = 0,
			direction = 'prev'
		} = options

		const transaction = this.getTransaction(storeName)
		const store = transaction.objectStore(storeName)
		const source = indexName ? store.index(indexName) : store

		return new Promise((resolve, reject) => {
			const request = source.openCursor(range, direction)
			const results = []
			let skipped = 0

			request.onsuccess = (event) => {
				const cursor = event.target.result

				if (cursor) {
					if (skipped < offset) {
						skipped++
						cursor.continue()
						return
					}

					if (results.length < limit) {
						results.push(cursor.value)
						cursor.continue()
					} else {
						resolve(results)
					}
				} else {
					resolve(results)
				}
			}

			request.onerror = () => reject(request.error)
		})
	}

	async delete (storeName, id) {
		await this.init()
		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName, 'readwrite')
			const store = transaction.objectStore(storeName)
			const request = store.delete(id)

			request.onsuccess = () => resolve()
			request.onerror = () => reject(request.error)
		})
	}

	async clear (storeName) {
		await this.init()
		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName, 'readwrite')
			const store = transaction.objectStore(storeName)
			const request = store.clear()

			request.onsuccess = () => resolve()
			request.onerror = () => reject(request.error)
		})
	}

	async count (storeName, indexName, range) {
		await this.init()
		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName)
			const store = transaction.objectStore(storeName)
			const source = indexName ? store.index(indexName) : store
			const request = source.count(range)

			request.onsuccess = () => resolve(request.result)
			request.onerror = () => reject(request.error)
		})
	}

	getLatestTimestampKey (storeName, stageId) {
		return `${storeName}_${stageId}`
	}

	async updateLatestTimestamp (storeName, stageId, timestamp) {
		const key = this.getLatestTimestampKey(storeName, stageId)
		const current = this.latestTimestamps.get(key) || 0

		if (timestamp > current) {
			// console.log(`latestTimestamps更新为${timestamp}`)
			this.latestTimestamps.set(key, timestamp)
		}
	}

	async getLatestTimestamp (storeName, stageId) {
		// console.log('getLatestTimestamp', stageId)
		const key = this.getLatestTimestampKey(storeName, stageId)

		// 先从缓存获取
		let latest = this.latestTimestamps.get(key)

		if (latest !== undefined) {
			return latest
		}

		await this.init()

		return new Promise((resolve, reject) => {
			const transaction = this.getTransaction(storeName)
			const store = transaction.objectStore(storeName)
			const index = store.index('stage_time')
			const range = IDBKeyRange.only([stageId])

			const request = index.openCursor(range, 'prev')

			request.onsuccess = (event) => {
				const cursor = event.target.result
				if (cursor) {
					const timestamp = cursor.value.timestamp
					this.latestTimestamps.set(key, timestamp)
					resolve(timestamp)
				} else {
					this.latestTimestamps.set(key, 0)
					resolve(0)
				}
			}

			request.onerror = () => reject(request.error)
		})
	}

	async shouldInsertMessage (storeName, message) {
		const {stage_id, timestamp} = message

		// 获取当前存储的最新时间戳
		const latestTimestamp = await this.getLatestTimestamp(storeName, stage_id)

		// 只有时间戳大于最新时间戳才插入
		return timestamp > latestTimestamp
	}

	async filterMessagesToInsert (storeName, messages) {
		const filteredMessages = []

		for (const message of messages) {
			if (await this.shouldInsertMessage(storeName, message)) {
				filteredMessages.push(message)
			}
		}

		return filteredMessages
	}

	close () {
		if (this.db) {
			this.db.close()
			this.db = null
		}
	}
}

export const getIndexedDB = () => IndexedDBManager.getInstance()

export default IndexedDBManager.getInstance()
